import { useCallback, useRef } from 'react';
import { useMessageStore } from '@/stores/message-store';
import { useUIStore } from '@/stores/ui-store';
import { useSaaSModule } from './use-saas-module';
import { parseCSVToRecipients } from '@/lib/utils/csv';
import type { Recipient } from '@/types/messaging';
import type { CRMRecord } from '@/types/saas';

// Numbers typed with commas or pasted together are added in the same millisecond,
// so the time alone gave them all one id — the counter keeps each id its own.
let manualRecipientSeq = 0;

/**
 * Hook for managing message recipients
 */
export function useRecipients() {
  const recipients = useMessageStore((s) => s.recipients);
  const addRecipient = useMessageStore((s) => s.addRecipient);
  const addRecipients = useMessageStore((s) => s.addRecipients);
  const removeRecipient = useMessageStore((s) => s.removeRecipient);
  const clearRecipients = useMessageStore((s) => s.clearRecipients);
  const { module: saasModule } = useSaaSModule();

  const addManualRecipient = useCallback(
    (name: string, phone: string) => {
      const recipient: Recipient = {
        id: `manual_${Date.now()}_${manualRecipientSeq++}`,
        name,
        phone,
        source: 'manual',
      };
      addRecipient(recipient);
    },
    [addRecipient]
  );

  const importFromCSV = useCallback(
    (
      csvContent: string,
      columnMap?: { phone: string; name?: string; email?: string },
      options?: { propertyPrefix?: string; sourceTag?: string },
    ) => {
      const parsed = parseCSVToRecipients(csvContent, columnMap, options);
      addRecipients(parsed);
      return parsed.length;
    },
    [addRecipients]
  );

  /**
   * Remove all recipients whose ID starts with `csv_{tag}_`.
   * Used when deleting a loaded sheet.
   */
  const removeRecipientsByTag = useCallback(
    (tag: string) => {
      const current = useMessageStore.getState().recipients;
      const remaining = current.filter((r) => !r.id.startsWith(`csv_${tag}_`));
      useMessageStore.setState({ recipients: remaining });
    },
    []
  );

  /** Records behind the latest search results, keyed `${module}_${id}` */
  const searchedRecordsRef = useRef(new Map<string, CRMRecord>());

  const searchCRM = useCallback(
    async (module: string, query: string) => {
      if (!saasModule) return [];
      const records = await saasModule.searchRecords(module, query);
      searchedRecordsRef.current = new Map(records.map((r) => [`${r.module}_${r.id}`, r]));
      // One record at a time: a batch call skips a number already seen on
      // an earlier record, so a lead sharing a number with another simply
      // vanished from the results. The dialog now lists it and marks it
      // "Same number as …"; addFromCRM still sends each number once.
      return records.flatMap((r) => saasModule.addRecordsAsRecipients([r]));
    },
    [saasModule]
  );

  const addFromCRM = useCallback(
    (crmRecipients: Recipient[]) => {
      // One message per number — the search list may now show the same
      // number on two leads (e.g. "+91 99…" and "9199…").
      const seen = new Set(useMessageStore.getState().recipients.map((r) => r.phone.replace(/\D/g, '')));
      addRecipients(crmRecipients.filter((r) => {
        const digits = r.phone.replace(/\D/g, '');
        if (!digits) return true;
        if (seen.has(digits)) return false;
        seen.add(digits);
        return true;
      }));
      // Keep the picked records so the phone-field pills can see every
      // field they hold — addRecipients drops a lead whose number is
      // already in the list, so the recipients alone can't tell that
      // Lead 2 has it under Phone while Lead 1 has it under Mobile.
      const picked = new Map<string, CRMRecord>();
      for (const r of crmRecipients) {
        const key = `${r.module}_${r.entityId}`;
        const record = searchedRecordsRef.current.get(key);
        if (record) picked.set(key, record);
      }
      if (picked.size > 0) {
        const pickedRows = crmRecipients.filter((r) => picked.has(`${r.module}_${r.entityId}`));
        useUIStore.getState().addCrmSearchRecords([...picked.values()], pickedRows);
      }
    },
    [addRecipients]
  );

  return {
    recipients,
    addManualRecipient,
    addRecipient,
    addRecipients,
    removeRecipient,
    removeRecipientsByTag,
    clearRecipients,
    importFromCSV,
    searchCRM,
    addFromCRM,
    count: recipients.length,
  };
}
